import React, { useCallback, useMemo, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import axios from "axios";
import UserAvatar from "../UserAvatar";
import { getToken } from "../../auth/token";
import { validateAvatarFile } from "../../utils/uploadValidation";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

export default function ProfileAvatarEditor({ user, onUpdated }) {
    const fileInputRef = useRef(null);
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [imageSrc, setImageSrc] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [error, setError] = useState("");
    const [uploading, setUploading] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [avatarVersion, setAvatarVersion] = useState(null);

    const displayName = user?.username || user?.email?.split("@")[0] || "Пользователь";
    const avatarPreviewUrl = useMemo(() => {
        if (!user?.avatarUrl) {
            return user?.avatarUrl;
        }
        if (!avatarVersion) {
            return user.avatarUrl;
        }
        const separator = user.avatarUrl.includes("?") ? "&" : "?";
        return `${user.avatarUrl}${separator}v=${avatarVersion}`;
    }, [user?.avatarUrl, avatarVersion]);

    const resetCropState = useCallback(() => {
        if (imageSrc) {
            URL.revokeObjectURL(imageSrc);
        }
        setImageSrc(null);
        setSelectedFile(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        setCropModalOpen(false);
        setError("");
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }, [imageSrc]);

    const onCropComplete = useCallback((_croppedArea, pixels) => {
        setCroppedAreaPixels(pixels);
    }, []);

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        const { file: validFile, error: validationError } = validateAvatarFile(file);
        if (validationError) {
            setError(validationError);
            return;
        }
        setError("");
        setSelectedFile(validFile);
        setImageSrc(URL.createObjectURL(validFile));
        setCropModalOpen(true);
    };

    const handleUpload = async () => {
        if (!selectedFile || !croppedAreaPixels) {
            setError("Выберите область обрезки");
            return;
        }

        setUploading(true);
        setError("");

        const formData = new FormData();
        formData.append("avatar", selectedFile);
        formData.append("cropX", String(Math.round(croppedAreaPixels.x)));
        formData.append("cropY", String(Math.round(croppedAreaPixels.y)));
        formData.append("cropWidth", String(Math.round(croppedAreaPixels.width)));

        const token = getToken();
        try {
            const response = await axios.post(`${API_BASE_URL}/api/me/avatar`, formData, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                withCredentials: true,
            });
            setAvatarVersion(Date.now());
            onUpdated?.(response.data);
            resetCropState();
        } catch (uploadError) {
            const message = uploadError?.response?.data?.error || "Не удалось загрузить аватар";
            setError(message);
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = async () => {
        if (!user?.avatarUrl) {
            return;
        }
        setRemoving(true);
        setError("");
        const token = getToken();
        try {
            const response = await axios.delete(`${API_BASE_URL}/api/me/avatar`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                withCredentials: true,
            });
            setAvatarVersion(Date.now());
            onUpdated?.(response.data);
        } catch (removeError) {
            const message = removeError?.response?.data?.error || "Не удалось удалить аватар";
            setError(message);
        } finally {
            setRemoving(false);
        }
    };

    return (
        <div className="profile-avatar-editor">
            <div className="profile-avatar-editor__preview">
                <UserAvatar
                    avatarUrl={avatarPreviewUrl}
                    name={displayName}
                    className="profile-avatar-editor__avatar"
                    size={96}
                />
            </div>
            <div className="profile-avatar-editor__actions">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png"
                    className="visually-hidden"
                    onChange={handleFileChange}
                />
                <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || removing}
                >
                    {user?.avatarUrl ? "Сменить фото" : "Загрузить фото"}
                </button>
                {user?.avatarUrl && (
                    <button
                        type="button"
                        className="ghost-btn"
                        onClick={handleRemove}
                        disabled={uploading || removing}
                    >
                        {removing ? "Удаление…" : "Удалить фото"}
                    </button>
                )}
            </div>
            <p className="profile-tip" style={{gridTemplateColumn: "none", width: "100%"}}>Подсказка: используйте JPG или PNG, до 5 МБ.</p>
            {error && <p className="form-error">{error}</p>}

            {cropModalOpen && imageSrc && (
                <div className="avatar-crop-modal" role="dialog" aria-modal="true" aria-label="Обрезка аватара">
                    <div className="avatar-crop-modal__backdrop" onClick={resetCropState} />
                    <div className="avatar-crop-modal__panel">
                        <h2 className="avatar-crop-modal__title">Обрезка фото профиля</h2>
                        <div className="avatar-crop-modal__cropper">
                            <Cropper
                                image={imageSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                cropShape="round"
                                showGrid={false}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                            />
                        </div>
                        <label className="avatar-crop-modal__zoom">
                            Масштаб
                            <input
                                style={{}}
                                type="range"
                                min={1}
                                max={3}
                                step={0.05}
                                value={zoom}
                                onChange={(event) => setZoom(Number(event.target.value))}
                            />
                        </label>
                        <div className="avatar-crop-modal__buttons">
                            <button type="button" className="ghost-btn" onClick={resetCropState} disabled={uploading}>
                                Отмена
                            </button>
                            <button type="button" className="toolbar-btn" onClick={handleUpload} disabled={uploading}>
                                {uploading ? "Загрузка…" : "Сохранить"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
